/**
 * SignUp — Auth Hub SSO sign-up page
 *
 * If Auth Hub is enabled, redirects to the centralised hub.
 * Otherwise shows a standard registration form.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import { useAuth } from '../../context/AuthContext';
import { authHubService } from '../../services/AuthHubService';
import Button from '../../components/shared/Button';
import Card from '../../components/shared/Card';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle, isAuthenticated, loading } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // If Auth Hub is enabled, redirect immediately
  useEffect(() => {
    if (authHubService.isEnabled()) {
      authHubService.redirectToSignup('/dashboard');
    }
  }, []);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await signUp(email, password, displayName);
      navigate('/dashboard');
    } catch {
      // Error handled in AuthContext
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch {
      // Error handled in AuthContext
    }
  };

  // Don't render form if Auth Hub is enabled
  if (authHubService.isEnabled()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-courtyard-parchment">
        <p className="text-courtyard-stone/80">Redirecting to sign up...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign Up | Finding Your Voice</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-courtyard-parchment px-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display text-3xl font-bold text-courtyard-stone text-center mb-2">
            Create Your Account
          </h1>
          <p className="text-center text-courtyard-stone/80 mb-8">
            Begin your journey to finding your voice
          </p>

          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="displayName"
                  className="block text-sm font-medium text-courtyard-stone/80 mb-1"
                >
                  Display Name
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-courtyard-stone/20 rounded-lg bg-white text-courtyard-stone focus:outline-none focus:ring-2 focus:ring-courtyard-gold/50"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-courtyard-stone/80 mb-1"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-courtyard-stone/20 rounded-lg bg-white text-courtyard-stone focus:outline-none focus:ring-2 focus:ring-courtyard-gold/50"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-courtyard-stone/80 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-courtyard-stone/20 rounded-lg bg-white text-courtyard-stone focus:outline-none focus:ring-2 focus:ring-courtyard-gold/50"
                  placeholder="At least 6 characters"
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-courtyard-stone/80 mb-1"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-courtyard-stone/20 rounded-lg bg-white text-courtyard-stone focus:outline-none focus:ring-2 focus:ring-courtyard-gold/50"
                  placeholder="Confirm your password"
                />
              </div>

              {formError && (
                <p className="text-sm text-red-600">{formError}</p>
              )}

              <Button
                variant="primary"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="my-4 flex items-center gap-3">
              <div className="flex-1 h-px bg-courtyard-stone/10" />
              <span className="text-xs text-courtyard-stone/80">or</span>
              <div className="flex-1 h-px bg-courtyard-stone/10" />
            </div>

            <Button
              variant="secondary"
              className="w-full"
              onClick={handleGoogleSignUp}
            >
              Sign up with Google
            </Button>
          </Card>

          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-courtyard-stone/80">
              Already have an account?{' '}
              <Link
                to="/signin"
                className="text-courtyard-gold-ink hover:underline"
              >
                Sign In
              </Link>
            </p>
            <Link
              to="/dashboard"
              className="text-sm text-courtyard-stone/80 hover:text-courtyard-stone/80"
            >
              Continue without account
            </Link>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default SignUp;
